import { CustomerReference } from '../queries/CustomerReference';
import { useQuoteApproval } from "../../viewmodels/price-drawer/useQuoteApproval";
import { customerTypeLook } from "../../customerTypeLook";
import { money } from "../../models/price-drawer/quotePricingPolicy";
import React from "react";
import {
  Modal, Button, Checkbox, InputNumber, Select, Input, Alert, Tabs, Spin, Tag, Avatar, Tooltip,
  message as antMessage, theme,
} from "antd";
import {
  SendOutlined, CheckCircleOutlined, CloseCircleOutlined,
  WarningOutlined, ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import AppTheme from "../../../theme/AppTheme";
import { useQv4ThemeState, Qv4ThemeCtx } from "../../helpers";
import { gpFromBase as gpFromSp } from "../../models/price-drawer/quotePricingPolicy";
import { OverviewTab } from "../workflow-drawer/OverviewTab";

dayjs.extend(relativeTime);
dayjs.extend(utc);

export function QuoteApprovalModal(props) {
  return <AppTheme><ThemedQuoteApprovalModal {...props} /></AppTheme>;
}

function ThemedQuoteApprovalModal({ open, onClose, approval, authState, userId, onDone, effectivePriority = null }) {
  const qv4Theme = useQv4ThemeState();
  const { t } = qv4Theme;
  const { token } = theme.useToken();
  const { sendOutcome, canDecide, payload, belowGpAction, deliveryEnabled, setDeliveryEnabled, setPartSp, partVat, partOldExchange, deliveryAmount, setDeliveryAmount, includedPartIds, activeTab, setActiveTab, messageText, setMessageText, keepAgentText, setKeepAgentText, textManuallyEdited, setTextManuallyEdited, rejectMode, setRejectMode, rejectReason, setRejectReason, approving, rejecting, isDirty, actionSnippet, qrLoading, bindingError, fullTask, fullTaskLoading, userMap, isCustomised, isB2B, isB2C, communicationChannel, manualChannel, pricingNotice, needsDedicatedCycle, pricedById, selectedPartsForInjection, deliveryPerPart, markDirty, togglePart, updateVat, toggleOldExchange, handleApprove, handleReject, policyLoading } = useQuoteApproval({ open, onClose, approval, authState, userId, onDone, effectivePriority, notify: antMessage });

  if (!approval) return null;

  const contextCol = (
    <div style={{ overflowY: "auto", paddingRight: 4, borderRight: `1px solid ${t.border}` }}>
      <div style={{ fontWeight: 600, fontSize: 12, color: t.textSub, marginBottom: 8 }}>Query Details</div>
      {fullTaskLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><Spin size="small" /></div>
      ) : fullTask?.task ? (
        <OverviewTab task={fullTask.task} afterSales={fullTask.after_sales} parts={fullTask.parts ?? []} userMap={userMap} />
      ) : (
        <div style={{ fontSize: 12, color: t.textSub }}>No additional query details available.</div>
      )}
    </div>
  );

  const leftCol = (
    <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, paddingRight: 4 }}>
      {/* Customer info */}
      <div style={{ padding: "8px 10px", background: t.hover, borderRadius: 6, fontSize: 12, flexShrink: 0 }}>
        <div style={{ color: t.textSub, marginBottom: 6 }}>
          <strong style={{ color: t.text }}>Query:</strong> #{payload.query_id}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <Avatar
            size={32}
            icon={customerTypeLook(isB2B ? 0 : isB2C ? 1 : null).icon}
            style={{ backgroundColor: customerTypeLook(isB2B ? 0 : isB2C ? 1 : null).color, flexShrink: 0 }}
          />
          <div>
            <div style={{ fontWeight: 600, color: t.text }}>{payload.customer_name || "—"}</div>
            <div style={{ color: t.textSub, fontSize: 11 }}>
              {isB2B ? "B2B · Garage" : isB2C ? "B2C · Retail" : "Type unknown"}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 11, color: t.textSub }}>
          Submitted by{" "}
          <strong style={{ color: t.text }}>{payload.submitted_by_name || "—"}</strong>
          {approval.submitted_at && (() => {
            const d = dayjs.utc(approval.submitted_at.replace(" ", "T"));
            return ` · ${d.format("DD MMM · hh:mm A")} · ${d.from(dayjs.utc().add(4, "hour"))}`;
          })()}
        </div>
        {approval.resubmission_reason && (
          <Alert
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            style={{ marginTop: 8, fontSize: 12 }}
            message={<><strong>Resubmission:</strong> {approval.resubmission_reason}</>}
          />
        )}
      </div>

      <div style={{ fontSize: 12 }}>Required priority: <strong>{approval.required_priority ?? 0}</strong></div>
      {(payload.approval_reasons || []).map(reason => <Tag key={reason} color="orange">{{ quote_send_permission_required: "Direct quote sharing requires approval", below_recommended_gp: "Below recommended GP" }[reason] || reason}</Tag>)}
      {pricingNotice && <Alert type="warning" showIcon message={pricingNotice} />}
      {sendOutcome && <Alert type="warning" showIcon message={sendOutcome.message} />}
      {needsDedicatedCycle && <Alert type="error" showIcon message="An ordinary approval cannot authorize prices below recommended GP. Restore the recommended base prices, or reject this request so the salesperson can submit a dedicated below-GP request." />}
      {/* Visible customer delivery is independent of internal policy allocation. */}
      <div style={{
        border: `1px solid ${token.colorInfoBorder}`, borderRadius: 6, padding: "8px 10px", flexShrink: 0,
        background: token.colorInfoBg,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <Checkbox checked={deliveryEnabled} onChange={e => { setDeliveryEnabled(e.target.checked); markDirty(); }}>Customer delivery</Checkbox>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <InputNumber
            value={deliveryAmount}
            min={0} disabled={!deliveryEnabled}
            size="small"
            prefix="AED"
            style={{ width: 110 }}
            onChange={v => { setDeliveryAmount(v ?? 0); markDirty(); }}
          />
          {selectedPartsForInjection.length > 1 && (
            <span style={{ fontSize: 11, color: t.textSub }}>
              ÷ {selectedPartsForInjection.length} = <strong>AED {(deliveryAmount / selectedPartsForInjection.length).toFixed(2)}/part</strong>
            </span>
          )}
        </div>
      </div>

      <div style={{ fontWeight: 600, fontSize: 12, color: t.textSub, flexShrink: 0 }}>Parts</div>

      {(payload.parts ?? []).map(p => {
        const checked = includedPartIds.has(p.query_part_id);
        const { pct = 5, mode = "excl" } = partVat[p.query_part_id] ?? {};
        const calculated = pricedById.get(Number(p.query_part_id));
      const sp = calculated.sp;
        const cp = calculated.cp ?? 0;
        const baseSp = calculated.base_sp;
        const gp = gpFromSp(cp, baseSp);
        const gpColor = gp > 0 ? token.colorSuccess : token.colorError;
        const share = checked ? deliveryPerPart : 0;
        const finalPrice = money(mode === "incl" ? (sp + share) * (1 + pct / 100) : (sp + share));
        const vatSuffix = mode === "incl" ? `(Incl. VAT ${pct}%)` : mode === "excl" ? `+ VAT ${pct}%` : "";
        const inclVatPrice = money((sp + share) * (1 + pct / 100));

        return (
          <div
            key={p.query_part_id}
            data-testid={`quote-approval-part-${p.query_part_id}`}
            style={{
              border: `1px solid ${checked ? token.colorSuccessBorder : t.border}`,
              borderRadius: 6, padding: "8px 10px",
              background: checked ? (token.colorSuccessBg) : undefined, flexShrink: 0,
              transition: "background 0.15s, border-color 0.15s",
            }}
          >
            <div style={{ display: "flex", alignItems: "stretch", gap: 10 }}>
              <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                <div
                  style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
                  onClick={() => togglePart(p.query_part_id)}
                >
                  <Checkbox checked={checked} onChange={() => {}} />
                  <span style={{ fontWeight: 600, fontSize: 12 }}>{p.part_name}</span>
                  <Tooltip title={Number(p.warranty_days || 0) > 0 ? `${p.warranty_days} days replacement warranty` : "No warranty"}>
                    <span style={{ color: t.textSub, fontSize: 11, cursor: "default" }}>
                      {p.condition || "New"}
                      {Number(p.warranty_days || 0) > 0 ? ` · ${p.warranty_days}d` : " · No warranty"}
                    </span>
                  </Tooltip>
                  <span onClick={e => e.stopPropagation()}>
                    <Checkbox
                      checked={partOldExchange[p.query_part_id] ?? !!p.old_exchange}
                      onChange={() => toggleOldExchange(p.query_part_id)}
                    >
                      <span style={{ fontSize: 11 }}>With Old Exchange</span>
                    </Checkbox>
                  </span>
                </div>
                <div style={{ marginTop: 6, marginLeft: 24, fontSize: 11, color: t.textSub, lineHeight: 1.7, display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }} onClick={e => e.stopPropagation()}>
                    <Tooltip title="Cost price — what the supplier quoted">
                      <span style={{ cursor: "default" }}>
                        CP: <strong style={{ color: t.text }}>AED {cp.toLocaleString()}</strong>
                      </span>
                    </Tooltip>
                    {" · "}
                    <Tooltip title="GP percentage on supplier cost, before internal allocation">
                      <span style={{ cursor: "default" }}>
                        GP: <strong style={{ color: gpColor }}>{gp.toFixed(1)}%</strong>
                      </span>
                    </Tooltip>
                    <span>Base</span>
                    <InputNumber
                      precision={2}
                      aria-label={`Base selling price for ${p.part_name}`}
                      value={baseSp}
                      min={0}
                      size="small"
                      prefix="AED"
                      style={{ width: 110, fontWeight: 600 }}
                      onChange={v => { setPartSp(prev => ({ ...prev, [p.query_part_id]: v ?? 0 })); markDirty(); }}
                    />
                    {checked && share > 0 && (
                      <Tooltip title={`Delivery share (${deliveryAmount} ÷ ${selectedPartsForInjection.length} part${selectedPartsForInjection.length > 1 ? "s" : ""})`}>
                        <span style={{ color: token.colorInfo, cursor: "default" }}>
                          +{share % 1 === 0 ? share : share.toFixed(2)} del
                        </span>
                      </Tooltip>
                    )}
                  </div>
                  {checked && <div style={{ padding: 8, borderRadius: 6, background: calculated.below_recommended_gp ? (token.colorErrorBg) : t.hover, color: t.text }}>
                    {calculated.below_recommended_gp && <Tag color="red">Below recommended GP</Tag>}
                    <div>Recommended GP: {calculated.recommended_gp_percentage}% · Base: AED {money(calculated.recommended_base_selling_price).toFixed(2)}</div>
                    <div>Submitted base: AED {money(baseSp).toFixed(2)} · Shortfall: AED {money(Math.max(0, calculated.recommended_base_selling_price - baseSp)).toFixed(2)}</div>
                    <div>Internal allocation: AED {money(calculated.internal_delivery_allocation).toFixed(2)}</div>
                    <div>Customer delivery: AED {money(share).toFixed(2)}</div>
                  </div>}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }} onClick={e => e.stopPropagation()}>
                    {/* App.css's global ".ant-select { width: 100% !important; }"
                        overrides any inline width on the Select itself, so the
                        fixed width has to live on this wrapper instead — the
                        Select then resolves its 100% against this div. */}
                    <div style={{ width: 168, flexShrink: 0 }}>
                      <Select
                        value={mode}
                        size="small"
                        onChange={v => updateVat(p.query_part_id, "mode", v)}
                        options={[
                          { value: "incl", label: "Including VAT" },
                          { value: "excl", label: "Excluding VAT" },
                          { value: "none", label: "No VAT" },
                        ]}
                      />
                    </div>
                    <InputNumber
                      value={pct} min={0} max={100} size="small" disabled={mode === "none"} suffix="%"
                      style={{ width: 70 }}
                      onChange={v => updateVat(p.query_part_id, "pct", v ?? 0)}
                    />
                  </div>
                </div>
              </div>
              {/* Spans all 3 rows to the left (name row + both detail rows),
                  vertically centered — this is the number that matters most
                  in this block. */}
              <div style={{ width: 1, alignSelf: "stretch", background: t.border, flexShrink: 0 }} />
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, flexShrink: 0, padding: "0 2px" }}>
                <Tooltip title="Final customer-facing price after SP + delivery + VAT">
                  <span style={{
                    color: token.colorSuccess, fontWeight: 700, fontSize: 20,
                    background: token.colorSuccessBg, border: `1px solid ${token.colorSuccessBorder}`,
                    borderRadius: 6, padding: "4px 12px", cursor: "default",
                    whiteSpace: "nowrap",
                  }}>
                    AED {finalPrice.toLocaleString()}
                  </span>
                </Tooltip>
                {vatSuffix && <span style={{ fontSize: 11, color: t.textSub }}>{vatSuffix}</span>}
                {mode !== "none" && (
                  <span style={{ fontSize: 10, color: t.textSub }}>Incl. VAT: AED {inclVatPrice.toLocaleString()}</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const rightCol = (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, minHeight: 0 }}>
      {manualChannel && (
        <Alert
          type="warning"
          showIcon
          message={`This customer/query uses ${communicationChannel.label} as the preferred communication channel. Approving will record that the quote was shared manually; Uzdpart360 will not send an automated WATI message`}
        />
      )}
      {isCustomised && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ flexShrink: 0, fontSize: 11 }}
          message={
            <div>
              <strong>Agent customised this message</strong> — the text was manually edited before submission.
              <div style={{ marginTop: 6, display: "flex", gap: 8 }}>
                <Button
                  size="small"
                  type={keepAgentText ? "primary" : "default"}
                  onClick={() => { setKeepAgentText(true); setTextManuallyEdited(false); setMessageText(payload.message_text ?? ""); }}
                >
                  Keep agent's text
                </Button>
                <Button
                  size="small"
                  type={!keepAgentText ? "primary" : "default"}
                  icon={<ReloadOutlined />}
                  loading={qrLoading}
                  onClick={() => { setKeepAgentText(false); setTextManuallyEdited(false); }}
                >
                  Use fresh template
                </Button>
              </div>
            </div>
          }
        />
      )}

      {!isCustomised && (
        <Tabs
          activeKey={activeTab}
          onChange={v => { setActiveTab(v); setTextManuallyEdited(false); markDirty(); }}
          size="small"
          style={{ flexShrink: 0 }}
          items={[
            { key: "b2c", label: "B2C · Retail" },
            { key: "b2b", label: "B2B · Garage" },
          ]}
        />
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        {textManuallyEdited && (
          <>
            <Tag color="orange" style={{ fontSize: 11, margin: 0 }}>Manually edited</Tag>
            <Tooltip title="Discard your edits and restore auto-generated text">
              <Button
                size="small" type="text"
                style={{ fontSize: 11, color: t.textSub, padding: "0 4px" }}
                onClick={() => { setTextManuallyEdited(false); }}
              >
                Reset to template
              </Button>
            </Tooltip>
          </>
        )}
      </div>

      {bindingError && <Alert type="error" message={bindingError} />}
      {qrLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 32 }}><Spin /></div>
      ) : (
        <Input.TextArea
          value={messageText}
          onChange={e => { setMessageText(e.target.value); setTextManuallyEdited(true); markDirty(); }}
          style={{
            fontSize: 12, fontFamily: "inherit", flex: 1, resize: "none", overflowY: "auto",
          }}
          placeholder="Message preview…"
        />
      )}
    </div>
  );

  return (
    <Qv4ThemeCtx.Provider value={qv4Theme}><Modal
      open={open}
      onCancel={onClose}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CheckCircleOutlined style={{ color: token.colorWarning }} />
          {belowGpAction ? "Review below-GP quote" : "Review quote"} — #{payload.query_id} <CustomerReference value={payload.customer_reference} />
          {isDirty && <Tag color="orange" style={{ fontSize: 10, margin: 0 }}>Modified</Tag>}
        </div>
      }
      style={{ top: 16, paddingBottom: 0 }}
      styles={{
        content: { height: "calc(100vh - 32px)", display: "flex", flexDirection: "column", padding: 0, overflow: "hidden" },
        header: { padding: "16px 24px", flexShrink: 0 },
        body: { flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column", padding: "0 24px" },
        footer: { padding: "12px 24px", flexShrink: 0 },
      }}
      footer={
        !canDecide ? <Button onClick={onClose}>Close</Button> : rejectMode ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Input.TextArea
              rows={2}
              placeholder="Reason for rejection (required)…"
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              autoFocus
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Button onClick={() => { setRejectMode(false); setRejectReason(""); }}>Cancel</Button>
              <Button
                danger type="primary"
                icon={<CloseCircleOutlined />}
                loading={rejecting}
                onClick={handleReject}
              >
                Confirm Rejection
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <Button danger icon={<CloseCircleOutlined />} onClick={() => setRejectMode(true)}>
              Reject
            </Button>
            <div style={{ display: "flex", gap: 8 }}>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={approving}
                disabled={!!sendOutcome || !messageText.trim() || qrLoading || !actionSnippet || !!bindingError || selectedPartsForInjection.length === 0 || fullTaskLoading || policyLoading || needsDedicatedCycle}
                onClick={handleApprove}
              >
                {manualChannel ? "Approve Quote Recording" : "Approve & Send"}{isDirty ? " (modified)" : ""}
              </Button>
            </div>
          </div>
        )
      }
      width={1080}
      destroyOnHidden
      zIndex={1060}
    >
      <div style={{ display: "grid", gridTemplateColumns: "260px 380px 1fr", gap: 16, flex: 1, minHeight: 0 }}>
        {contextCol}
        {leftCol}
        {rightCol}
      </div>
    </Modal></Qv4ThemeCtx.Provider>
  );
}
